import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CodeConfirmationForm } from '@/ui/components/CodeConfirmationForm/CodeConfirmationForm';
import { key } from '@/assets/images/emojis';
import { routes } from '@/routes/routes';
import { useError } from '@/providers/Error';
import { bcPhoneMessages } from '@/constants/errorMessages';
import { useScoring } from '../../hooks/useScoring';
import { getPhoneVerificationErrorMessage } from '../../hooks/scoringUtils';
import { BcAttemptState, PhoneVerificationError } from '../../types/scoring';
import type { BcPhoneCodeState } from '../BcPhoneSelectPage/BcPhoneSelectPage';
import styles from './BcPhoneCodePage.module.scss';

const CODE_LENGTH = 4;
const RESEND_DELAY = 120;

export function BcPhoneCodePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { error, setError } = useError();
  const { sendVerificationCode, generateByCode, isGeneratingByCode } =
    useScoring();
  const [code, setCode] = useState('');

  const phone = (location.state as BcPhoneCodeState | null)?.phone;

  // The chosen phone only lives in navigation state; a reload starts over.
  if (!phone) {
    return <Navigate to={routes.scoring.phones} replace />;
  }

  const handleResendCode = async () => {
    try {
      const attempt = await sendVerificationCode(phone.id);
      if (attempt.phoneVerificationError !== PhoneVerificationError.None) {
        setError(bcPhoneMessages.sms_failed);
      }
    } catch {
      setError(bcPhoneMessages.sms_failed);
    }
  };

  const handleCodeChange = async (value: string) => {
    setCode(value);

    if (value.length !== CODE_LENGTH) {
      return;
    }

    try {
      const attempt = await generateByCode(value);
      if (attempt.bcAttemptState === BcAttemptState.Completed) {
        navigate(routes.wizard.base);
        return;
      }

      // Wrong or expired codes answer HTTP 200 with the reason set.
      setError(getPhoneVerificationErrorMessage(attempt.phoneVerificationError));
    } catch {
      setError(bcPhoneMessages.unexpected);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <img src={key} alt="Key emoji" className={styles.emoji} />
        <h1 className={styles.title}>Introdu codul de confirmare</h1>
        <p className={styles.subtitle}>
          Am trimis codul de confirmare către {phone.phone} (
          <Link to={routes.scoring.phones} className={styles.changeNumberLink}>
            Schimbă numărul
          </Link>
          )
        </p>
      </div>

      <CodeConfirmationForm
        value={code}
        className={styles.codeConfirmationForm}
        onChange={handleCodeChange}
        onResendCode={handleResendCode}
        isLoading={isGeneratingByCode}
        length={CODE_LENGTH}
        resendDelay={RESEND_DELAY}
        error={error}
        autoFocus
      />
    </div>
  );
}

export default BcPhoneCodePage;
